import { useRef, useState } from "react";
import { Flame, Wind, Sun, Cpu, Thermometer, Snowflake, Wrench, ClipboardCheck, HardHat, Camera, X, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { uploadFile, errMsg } from "@/lib/api";

const EQ_ICONS = {
  stufa_pellet: Flame, caldaia: Thermometer, pompa_calore: Wind, climatizzatore: Snowflake,
  solare_termico: Sun, building_automation: Cpu, altro: Wrench,
};

const KINDS = [
  { key: "intervento", label: "Intervento tecnico", desc: "Guasto, manutenzione o codice di errore", Icon: Wrench },
  { key: "sopralluogo", label: "Sopralluogo", desc: "Valutazione per un nuovo impianto", Icon: ClipboardCheck },
  { key: "cantiere", label: "Cantiere", desc: "Lavoro programmato (solo admin)", Icon: HardHat },
];

function PhotoUpload({ form, set }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    setBusy(true);
    try {
      for (const f of files.slice(0, 5 - form.photos.length)) {
        const res = await uploadFile("/public/upload", f);
        set((p) => ({ ...p, photos: [...p.photos, { ...res, preview: URL.createObjectURL(f) }] }));
      }
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-3">
        {form.photos.map((p, i) => (
          <div key={p.file_id} className="relative h-20 w-20 overflow-hidden rounded-xl border" data-testid={`error-photo-${i}`}>
            <img src={p.preview} alt="" className="h-full w-full object-cover" />
            <button type="button" data-testid={`error-photo-remove-${i}`}
              onClick={() => set((s) => ({ ...s, photos: s.photos.filter((x) => x.file_id !== p.file_id) }))}
              className="absolute right-1 top-1 rounded-full bg-black/60 p-0.5 text-white"><X className="h-3 w-3" /></button>
          </div>
        ))}
        {form.photos.length < 5 && (
          <button type="button" onClick={() => ref.current?.click()} disabled={busy} data-testid="error-photo-upload-button"
            className="choice grid h-20 w-20 place-items-center rounded-xl border-2 border-dashed border-slate-300 text-slate-500 hover:border-[#61a229] hover:text-[#61a229]">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Camera className="h-5 w-5" />}
          </button>
        )}
      </div>
      <input ref={ref} type="file" accept="image/*" multiple hidden onChange={onPick} data-testid="error-photo-input" />
      <p className="text-xs text-slate-500">Foto della targhetta della macchina o del codice di errore sul display (max 5).</p>
    </div>
  );
}

export function ServiceStep({ form, set, config, allowCantiere = false }) {
  const upd = (k) => (e) => set((p) => ({ ...p, [k]: e.target.value }));
  const kinds = KINDS.filter((k) => allowCantiere || k.key !== "cantiere");
  return (
    <div className="space-y-8">
      <div className={`grid gap-3 ${allowCantiere ? "sm:grid-cols-3" : "sm:grid-cols-2"}`}>
        {kinds.map(({ key, label, desc, Icon }) => (
          <button key={key} type="button" data-testid={`kind-option-${key}`} data-active={form.kind === key}
            onClick={() => set((p) => ({ ...p, kind: key }))}
            className="choice flex items-start gap-4 rounded-2xl border bg-white p-5 text-left">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-[#0f1710] text-[#a2ca6b]"><Icon className="h-5 w-5" /></span>
            <span>
              <span className="block font-display text-lg font-semibold text-slate-900">{label}</span>
              <span className="block text-sm text-slate-500">{desc}</span>
            </span>
          </button>
        ))}
      </div>

      {form.kind !== "sopralluogo" && (
        <div className="space-y-3">
          <Label className="text-sm font-semibold">Tipo di apparecchiatura {form.kind === "intervento" && "*"}</Label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
            {(config?.equipment || []).map((eq) => {
              const Icon = EQ_ICONS[eq.key] || Wrench;
              return (
                <button key={eq.key} type="button" data-testid={`equipment-option-${eq.key}`} data-active={form.equipment_type === eq.key}
                  onClick={() => set((p) => ({ ...p, equipment_type: eq.key }))}
                  className="choice flex flex-col items-start gap-2 rounded-xl border bg-white p-3.5 text-left">
                  <Icon className="h-5 w-5 text-[#61a229]" />
                  <span className="text-sm font-medium text-slate-800">{eq.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {form.kind === "intervento" && (
        <div className="grid gap-6 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="brand_model" className="text-sm font-semibold">Marca / Modello</Label>
            <Input id="brand_model" value={form.brand_model} onChange={upd("brand_model")} placeholder="Es. Palazzetti Ecofire Ginger"
              data-testid="brand-model-input" />
            <p className="text-xs text-slate-500">Indica marca/modello <b>oppure</b> carica una foto.</p>
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-semibold">Foto macchina / codice errore</Label>
            <PhotoUpload form={form} set={set} />
          </div>
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="notes" className="text-sm font-semibold">Descrizione del problema / note</Label>
        <Textarea id="notes" rows={3} value={form.notes} onChange={upd("notes")} data-testid="notes-input"
          placeholder="Descrivi brevemente cosa succede o cosa ti serve" />
      </div>
    </div>
  );
}
